import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { LinkIcon } from "lucide-react";
import api from "apiSingleton";
import { SEARCH_SOURCE } from "lookup";
import {
  usePerfectFitJobs,
  PerfectFitJobsTable,
} from "components/PerfectFitJobs/shared";
import UserInfo from "components/UserCardList/components/UserCardResult/molecules/UserInfo";
import UserContacts from "components/UserCardList/components/UserCardResult/molecules/UserContacts";
import CollapsibleSection from "components/UserCardList/components/UserCardResult/molecules/CollapsibleSection";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const USER_OBJECT_ID_PATTERN =
  /^User#[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const escapeFilterValue = (value) =>
  String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const quoteFilterValue = (value) => `"${escapeFilterValue(value)}"`;

const decodeUserIdParam = (userId) => {
  if (!userId) {
    return null;
  }

  try {
    return decodeURIComponent(userId);
  } catch {
    return null;
  }
};

const getUserIdFromIdentifier = (identifier) => {
  if (!identifier) {
    return null;
  }

  if (USER_OBJECT_ID_PATTERN.test(identifier)) {
    return identifier.slice("User#".length);
  }

  if (UUID_PATTERN.test(identifier)) {
    return identifier;
  }

  return null;
};

const getUserSearchFilter = (userId) => {
  if (UUID_PATTERN.test(userId)) {
    return `(objectID:${quoteFilterValue(
      userId
    )} OR objectID:${quoteFilterValue(`User#${userId}`)})`;
  }

  if (USER_OBJECT_ID_PATTERN.test(userId)) {
    const bareId = userId.slice("User#".length);

    return `(objectID:${quoteFilterValue(
      userId
    )} OR objectID:${quoteFilterValue(bareId)})`;
  }

  return `username:${quoteFilterValue(userId)}`;
};

const normalizeUserHit = (hit, decodedUrlUserId) => {
  const id =
    hit.id ||
    getUserIdFromIdentifier(hit.objectID) ||
    getUserIdFromIdentifier(decodedUrlUserId);

  return id ? { ...hit, id } : hit;
};

const ReverseMatch = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const {
    selectedJobs,
    creatingMatches,
    expandedJobId,
    setExpandedJobId,
    perfectFitJobs,
    jobsLoading,
    loadPerfectFitJobs,
    getJobLocation,
    getJobRateSalary,
    getJobTalentRateSalary,
    getPerfectJobFitReason,
    handleSelectAll,
    handleSelectRow,
    createMatchForJobs,
  } = usePerfectFitJobs(user?.id);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const decodedUserId = decodeUserIdParam(userId);

        if (!decodedUserId) {
          setError({ message: "Invalid user ID in URL" });
          return;
        }

        const requests = [
          {
            indexName: process.env.REACT_APP_OS_USERS_INDEX_NAME,
            params: {
              filters: getUserSearchFilter(decodedUserId),
              hitsPerPage: 1,
              page: 0,
            },
          },
        ];

        const searchParams = {
          requests: JSON.stringify(requests),
          searchSource: SEARCH_SOURCE.OPEN_SEARCH,
        };

        const results = await api.proxy.search(searchParams);

        if (!results) {
          setError({ message: "Failed to load user" });
          return;
        }

        if (results?.results?.[0]?.hits?.length > 0) {
          const fetchedUser = normalizeUserHit(
            results.results[0].hits[0],
            decodedUserId
          );

          if (!fetchedUser.id) {
            setError({ message: `User not found: ${decodedUserId}` });
            return;
          }

          setUser(fetchedUser);
        } else {
          setError({ message: `User not found: ${decodedUserId}` });
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        setError({ message: err.message || "Failed to load user" });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  useEffect(() => {
    if (user?.id) {
      loadPerfectFitJobs({ variables: { userId: user.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loadPerfectFitJobs]);

  const handleCreateMatches = async () => {
    await createMatchForJobs(user.id, user.isUnclaimed);
  };

  if (!loading && !userId) {
    return (
      <div className="flex justify-center pt-8">
        <p className="text-red-500">No user ID provided</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center pt-8">
        <span className="loader"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center pt-8">
        <p className="text-red-500">Error loading user: {error.message}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center pt-8">
        <p className="text-red-500">{error?.message || "User not found"}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* User Profile Card - reusing existing components without action buttons */}
      <div className="talent-card rounded-t-lg flex flex-col bg-white font-inter mb-6">
        <div className="rounded-t-lg flex flex-wrap md:flex-nowrap border border-[#E5E7EB] p-4 box-border w-full gap-x-2">
          <div>
            <UserContacts hit={user} />
          </div>

          <div className="w-full lg:w-[85%] md:w-3/4 xl:w-full">
            <UserInfo
              hit={user}
              collectionKey="hits"
              activeTabName="SEARCHRESULTS"
              hideActionIcons={true}
            />
            <div className="max-w-fit lg:max-w-[85%] xl:max-w-2xl 2xl:max-w-4xl">
              <CollapsibleSection
                activeTabName="SEARCHRESULTS"
                hit={user}
                allowLocalHightLight={false}
                expandSkillsByDefault={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Perfect Fit Jobs Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Perfect Fit Jobs</h2>
          {perfectFitJobs.length > 0 && (
            <Button
              disabled={selectedJobs.size === 0 || creatingMatches}
              onClick={handleCreateMatches}
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              {!creatingMatches && <>Match to {selectedJobs.size} Job(s)</>}
              {creatingMatches && <>Creating Matches...</>}
            </Button>
          )}
        </div>

        <div className="-mx-6 no-scrollbar max-h-[50vh] overflow-y-auto px-6 bg-gray-100">
          <PerfectFitJobsTable
            perfectFitJobs={perfectFitJobs}
            jobsLoading={jobsLoading}
            selectedJobs={selectedJobs}
            expandedJobId={expandedJobId}
            setExpandedJobId={setExpandedJobId}
            handleSelectAll={handleSelectAll}
            handleSelectRow={handleSelectRow}
            getJobLocation={getJobLocation}
            getJobRateSalary={getJobRateSalary}
            getJobTalentRateSalary={getJobTalentRateSalary}
            getPerfectJobFitReason={getPerfectJobFitReason}
          />
        </div>
      </div>
    </div>
  );
};

export default ReverseMatch;
